import { useDownloadArtworksHdArchive } from "@/api/artwork/artwork";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "@/hooks/use-toast";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { isAxiosError } from "axios";
import { Download, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

type DownloadArtworksHdButtonProps = {
  selectedArtworksIds: string[];
};

/** Selection action: downloads, as one zip, every HD image of the selected artworks. */
const DownloadArtworksHdButton = ({
  selectedArtworksIds,
}: DownloadArtworksHdButtonProps) => {
  const { t } = useTranslation();
  const vt = useVisibleT();
  const { mutate: downloadHdArchive, isPending } =
    useDownloadArtworksHdArchive();

  const onClick = () =>
    downloadHdArchive(selectedArtworksIds, {
      onSuccess: ({ file, filename }) => {
        const url = URL.createObjectURL(file);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename ?? "oeuvres-hd.zip";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      },
      onError: (error) => {
        if (isAxiosError(error) && error.response?.status === 404) {
          toast({
            variant: "error",
            title: vt("bo.artwork.downloadHd.empty", "Aucune image HD"),
            description: vt(
              "bo.artwork.downloadHd.emptyDescription",
              "Les œuvres sélectionnées n'ont aucune image HD.",
            ),
          });
          return;
        }
        console.error(error);
        toast({
          variant: "error",
          title: t("error"),
          description: t("errorDescription"),
        });
      },
    });

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          className="flex flex-row gap-2 items-center font-semibold text-sm"
          disabled={isPending || selectedArtworksIds.length === 0}
          onClick={onClick}
        >
          {isPending ? <Loader2 className="animate-spin" /> : <Download />}
          {vt("bo.artwork.downloadHd", "Télécharger les HD")}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top" variant="action" className="px-3">
        <span className="text-white text-sm font-medium">
          {vt(
            "bo.tooltip.downloadHd",
            "Télécharger les images haute définition des œuvres sélectionnées",
          )}
        </span>
      </TooltipContent>
    </Tooltip>
  );
};

export default DownloadArtworksHdButton;
